import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, FileText, ArrowLeft } from 'lucide-react';
import { useQuoteTemplates } from '@/hooks/useQuoteTemplates';
import { QuoteTemplateModal } from '@/components/crm/QuoteTemplateModal';
import { TemplateCard } from '@/components/crm/quote/TemplateCard';
import { QuoteTemplate } from '@/types/quote';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';

export default function CRMQuoteTemplates() {
  const navigate = useNavigate();
  const { templates, loading, createTemplate, updateTemplate, deleteTemplate } = useQuoteTemplates();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<QuoteTemplate | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<QuoteTemplate | null>(null);

  const handleEdit = (template: QuoteTemplate) => {
    navigate(`/crm/quote-templates/${template.id}`);
  };

  const handleNew = () => {
    navigate('/crm/quote-templates/new');
  };

  const handleDuplicate = async (template: QuoteTemplate) => {
    try {
      await createTemplate({
        name: `${template.name} (kopie)`,
        description: template.description,
        template_body: template.template_body,
        template_terms: template.template_terms,
        header_logo_url: template.header_logo_url,
        background_image_url: template.background_image_url,
        brand_primary: template.brand_primary,
        brand_secondary: template.brand_secondary,
        footer_text: template.footer_text,
        pdf_template_url: template.pdf_template_url,
        pdf_page_count: template.pdf_page_count,
        placeholder_positions: template.placeholder_positions,
        // Include all multi-page and content fields
        page_count: template.page_count,
        page_backgrounds: template.page_backgrounds,
        default_project_goal: template.default_project_goal,
        default_work_description: template.default_work_description,
        default_advice_conclusion: template.default_advice_conclusion,
        default_payment_conditions: template.default_payment_conditions,
        terms_document_url: template.terms_document_url,
        is_default: false,
      });
      toast.success(`Template "${template.name}" gedupliceerd`);
    } catch (error) {
      toast.error('Fout bij dupliceren template');
    }
  };

  const handleSave = async (data: Partial<QuoteTemplate>) => {
    if (selectedTemplate) {
      await updateTemplate(selectedTemplate.id, data);
    } else {
      await createTemplate(data);
    }
  };

  const handleDeleteClick = (template: QuoteTemplate) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (templateToDelete) {
      await deleteTemplate(templateToDelete.id);
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
    }
  };

  // Remove seed functions - templates should be created manually or duplicated

  return (
    <>
      <div className="space-y-6">
        {/* Breadcrumb Navigation */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink 
                onClick={() => navigate('/crm/quotes')}
                className="cursor-pointer hover:text-foreground"
              >
                Offertes
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Templates</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/crm/quotes')}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Offerte Templates</h1>
              <p className="text-muted-foreground">
                Beheer templates voor offertes met branding en standaard teksten
              </p>
            </div>
          </div>
          <Button onClick={handleNew}>
            <Plus className="h-4 w-4 mr-2" />
            Nieuw Template
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="font-semibold mb-2">Nog geen templates aangemaakt</h3>
              <p className="text-muted-foreground mb-4 text-sm text-center max-w-md">
                Maak je eerste offerte template aan om snel professionele offertes te genereren 
                met je eigen branding en standaard teksten.
              </p>
              <Button onClick={handleNew}>
                <Plus className="h-4 w-4 mr-2" />
                Eerste Template Aanmaken
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {templates.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={handleDeleteClick}
              />
            ))}
          </div>
        )}
      </div>

      <QuoteTemplateModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        template={selectedTemplate}
        onSave={handleSave}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Template verwijderen?</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je het template "{templateToDelete?.name}" wilt verwijderen?
              Deze actie kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground">
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
